import React, { useState } from "react";
import { G, TYPE, FONT_FAMILY, OVERVIEW } from "../../constants/theme";
import { jiraUrl } from "../../constants/api";
import { priorityCol, priorityTone } from "../../utils/colors";
import Description from "../typography/Description";
import Overline from "../typography/Overline";
import SectionTitle from "../typography/SectionTitle";
import ScoreBreakdown from "../common/ScoreBreakdown";
import OverviewSection from "../overview/OverviewSection";

const MONO = TYPE.mono.fontFamily;

function priorityAccentBorder(priority) {
  // Uniform, on-theme rail — the priority itself is shown by the pill next to it.
  return priority ? G.brand : G.gray200;
}

function StatusPill({ label, dark = false }) {
  const tone = priorityTone(label);
  const { bg, c } = priorityCol(label);
  const themed = dark
    ? {
        background: tone ? tone.darkBg : "rgba(255,255,255,0.08)",
        color: tone ? tone.darkC : "rgba(255,255,255,0.6)",
        border: `1px solid ${tone ? tone.darkBorder : "rgba(255,255,255,0.14)"}`,
      }
    : {
        background: tone ? tone.bg : bg,
        color: tone ? tone.c : c,
        border: `1px solid ${tone ? tone.border : "transparent"}`,
      };
  return (
    <span style={{
      padding: "2px 7px", borderRadius: 3,
      fontSize: 10, fontWeight: 700,
      letterSpacing: "0.04em", textTransform: "uppercase", flexShrink: 0,
      ...themed,
    }}>
      {label}
    </span>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <Overline style={{ marginBottom: 3, color: G.gray400 }}>{label}</Overline>
      <div style={{ fontSize: 13, color: G.gray700, lineHeight: 1.4 }}>
        {value || "—"}
      </div>
    </div>
  );
}

function CriticalDetail({ item }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px", paddingTop: 14 }}>
      <DetailField label="Status" value={item.status} />
      <DetailField label="Assigned To" value={item.assignedTo} />
      <DetailField label="Age" value={item.ageingDays ? `${item.ageingDays} days` : null} />
      <DetailField label="Priority" value={item.priority} />
      {item.riskScore && (
        <div style={{ gridColumn: "span 2" }}>
          <Overline style={{ marginBottom: 4, color: G.gray400 }}>Risk Score</Overline>
          <span style={{
            display: "inline-block", padding: "3px 10px", borderRadius: 2,
            background: item.riskScore > 80 ? G.red50 : G.amber50,
            color: item.riskScore > 80 ? G.red600 : G.amber600,
            fontSize: 12, fontWeight: 700,
          }}>
            {item.riskScore}
          </span>
        </div>
      )}
      {item.reasons?.length > 0 && (
        <div style={{ gridColumn: "span 2" }}>
          <Overline style={{ marginBottom: 6, color: G.gray400 }}>Why Flagged</Overline>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {item.reasons.map((r, i) => (
              <div key={i} style={{ fontSize: 12, color: G.red600, paddingLeft: 8, borderLeft: `2px solid ${G.red600}` }}>{r}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AgeingDetail({ item }) {
  return (
    <div style={{ paddingTop: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px", marginBottom: 12 }}>
        <DetailField label="Created" value={item.createdDate} />
        <DetailField label="Assigned" value={item.assignedTo} />
        <DetailField label="Status" value={item.status} />
        <DetailField label="Age" value={item.ageingDays ? `${item.ageingDays} days` : null} />
      </div>
      {item.slaTarget && (
        <div style={{ background: G.gray50, borderRadius: 2, padding: "10px 14px", marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: G.gray700, marginBottom: 4 }}>
            <strong>SLA Target:</strong> {item.slaTarget}d
            {item.overdueDays > 0 && (
              <span style={{ color: G.red600, marginLeft: 8, fontWeight: 700 }}>+{item.overdueDays}d overdue</span>
            )}
          </div>
          {item.breachDate && <div style={{ fontSize: 11, color: G.gray500 }}>Breach date: {item.breachDate}</div>}
        </div>
      )}
      {item.recommendedAction && (
        <div>
          <Overline style={{ marginBottom: 4, color: G.gray400 }}>Recommended Action</Overline>
          <div style={{ fontSize: 12, color: G.green600, fontWeight: 500 }}>{item.recommendedAction}</div>
        </div>
      )}
    </div>
  );
}

function QuickWinDetail({ item }) {
  return (
    <div style={{ paddingTop: 14 }}>
      <div style={{ background: "#EEF4FF", borderRadius: 2, padding: "12px 16px", marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#2563EB", marginBottom: 4 }}>Quick Win Score</div>
        <div style={{ fontSize: 28, fontWeight: 700, color: "#2563EB" }}>{item.winScore}/100</div>
      </div>
      {item.scoreBreakdown?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Overline style={{ marginBottom: 6, color: G.gray400 }}>Score Breakdown</Overline>
          {item.scoreBreakdown.map((s, i) => (
            <div key={i} style={{ fontSize: 12, color: G.gray600, paddingLeft: 8, borderLeft: `2px solid ${G.green600}`, marginBottom: 3 }}>{s}</div>
          ))}
        </div>
      )}
      {item.reason && (
        <div>
          <Overline style={{ marginBottom: 4, color: G.gray400 }}>Why Quick Win</Overline>
          <div style={{ fontSize: 12, color: G.gray600 }}>{item.reason}</div>
        </div>
      )}
    </div>
  );
}

function ExpandableItem({ item, type, isExpanded, onToggle, onTicketClick }) {
  const DetailView = type === "critical" ? CriticalDetail : type === "ageing" ? AgeingDetail : QuickWinDetail;
  return (
    <div style={{ padding: "12px 0", borderBottom: `1px solid ${G.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
            <span onClick={() => onTicketClick && onTicketClick(item.ticket)}
              style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: G.brand, cursor: "pointer" }}>
              {item.ticket}
            </span>
            {item.priority && <StatusPill label={item.priority} />}
          </div>
          <div style={{ fontSize: 12, color: G.gray500 }}>{item.detail}</div>
        </div>
        <button onClick={onToggle}
          style={{ padding: "4px 10px", borderRadius: 2, border: `1px solid ${G.border}`,
            background: "transparent", color: G.gray500, fontSize: 11, cursor: "pointer",
            fontFamily: FONT_FAMILY, marginLeft: 10, flexShrink: 0 }}>
          {isExpanded ? "Close" : "Details"}
        </button>
      </div>
      {isExpanded && <DetailView item={item} />}
    </div>
  );
}

function RepresentativeTicket({ item, type, onTicketClick }) {
  if (!item) return <div style={{ fontSize: 12, color: G.gray300, fontStyle: "italic" }}>No tickets</div>;
  let meta = "";
  if (type === "critical") {
    const parts = [];
    if (item.ageingDays) parts.push(`${item.ageingDays}d`);
    if (item.status) parts.push(item.status);
    meta = parts.join(" · ");
  } else if (type === "ageing") {
    const parts = [];
    if (item.ageingDays) parts.push(`${item.ageingDays}d ageing`);
    if (item.riskLevel) parts.push(item.riskLevel);
    meta = parts.join(" · ");
  } else {
    meta = item.winScore ? `Win score ${item.winScore}/100` : "";
  }
  return (
    <div>
      <div style={{ marginBottom: 5 }}>
        <span onClick={() => onTicketClick && onTicketClick(item.ticket)}
          style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: G.brand, cursor: "pointer" }}>
          {item.ticket}
        </span>
      </div>
      {item.detail && (
        <div style={{ fontSize: 13, color: G.gray600, lineHeight: 1.4, marginBottom: 5,
          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
          {item.detail}
        </div>
      )}
      {meta && <div style={{ fontSize: 11, color: G.gray400 }}>{meta}</div>}
    </div>
  );
}

function AttentionPanel({ label, desc, count, countUnit, items = [], accentColor, type, onTicketClick, breakdown, expandHelp }) {
  const [viewAll, setViewAll] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [hov, setHov] = useState(false);
  const rep = items[0] || null;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flex: 1, minWidth: 0,
        background: G.white,
        border: `1px solid ${hov ? G.brand : OVERVIEW.cardBorder}`,
        borderRadius: OVERVIEW.cardRadius,
        boxShadow: OVERVIEW.cardShadow,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "border-color 0.14s",
      }}
    >
      <div style={{ height: 3, background: accentColor, flexShrink: 0 }} />

      <div style={{ padding: "28px 28px 24px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: accentColor, flexShrink: 0 }} />
          <Overline style={{ color: G.gray500 }}>{label}</Overline>
        </div>
        {desc && <Description style={{ marginBottom: 16 }}>{desc}</Description>}

        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 52, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1, color: G.gray900 }}>
            {count}
          </span>
        </div>

        <div style={{ fontSize: 13, color: G.gray500, marginBottom: 16, lineHeight: 1.4 }}>
          {countUnit}
        </div>

        {breakdown && <ScoreBreakdown {...breakdown} style={{ marginBottom: 20 }} />}

        <div style={{ height: 1, background: G.border, marginBottom: 20 }} />

        <RepresentativeTicket item={rep} type={type} onTicketClick={onTicketClick} />

        <div style={{ height: 1, background: G.border, margin: "20px 0 0" }} />

        <div style={{ marginTop: "auto", paddingTop: 16 }}>
          <button
            onClick={() => { setViewAll(v => !v); setExpandedIdx(null); }}
            style={{
              background: "none", border: `1px solid ${OVERVIEW.cardBorder}`,
              padding: "8px 14px", borderRadius: OVERVIEW.panelRadius,
              fontSize: 12, fontWeight: 600, color: G.brand,
              cursor: "pointer", fontFamily: FONT_FAMILY, width: "100%",
              textAlign: "left", transition: "border-color 0.12s, color 0.12s, background 0.12s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = G.brand; e.currentTarget.style.background = G.brandSubtle; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = OVERVIEW.cardBorder; e.currentTarget.style.background = "none"; }}
          >
            {viewAll ? "Collapse ↑" : `View All ${count} →`}
          </button>
        </div>
      </div>

      {viewAll && items.length > 0 && (
        <div className="themed-scroll-light" style={{ borderTop: `1px solid ${G.border}`, padding: "0 28px", maxHeight: "min(60vh, 480px)", overflowY: "auto" }}>
          {expandHelp && (
            <Description style={{ padding: "14px 0 4px" }}>{expandHelp}</Description>
          )}
          {items.map((item, idx) => (
            <ExpandableItem key={idx} item={item} type={type}
              isExpanded={expandedIdx === idx}
              onToggle={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
              onTicketClick={onTicketClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GhostBtn({ children, href, onClick }) {
  const s = {
    padding: "6px 14px", borderRadius: 3, border: "1px solid #1A3557",
    background: "#071A2F", color: "#FFFFFF", fontSize: 11, fontWeight: 600,
    cursor: "pointer", fontFamily: FONT_FAMILY, whiteSpace: "nowrap",
    textDecoration: "none", display: "inline-block",
    transition: "background 0.12s, border-color 0.12s",
    letterSpacing: "0.02em",
  };
  const enter = e => { e.currentTarget.style.background = "#1A3557"; e.currentTarget.style.borderColor = "#2563EB"; };
  const leave = e => { e.currentTarget.style.background = "#071A2F"; e.currentTarget.style.borderColor = "#1A3557"; };
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" style={s} onMouseEnter={enter} onMouseLeave={leave}>{children}</a>;
  return <button style={s} onClick={onClick} onMouseEnter={enter} onMouseLeave={leave}>{children}</button>;
}

function PriorityCard({ rank, ticket, priority, age, score, breakdown, summary, isLast, onTicketClick }) {
  const accentColor = priorityAccentBorder(priority);
  const reasons = (breakdown || []).slice(0, 4);

  return (
    <div style={{
      display: "flex",
      alignItems: "stretch",
      minHeight: 172,
      borderBottom: isLast ? "none" : `1px solid ${G.border}`,
      borderLeft: `3px solid ${accentColor}`,
    }}>
      {/* LEFT — Ticket Identity */}
      <div style={{
        flex: "0 0 30%",
        padding: "22px 26px",
        background: "#071A2F",
        borderRight: "1px solid #1A3557",
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.55)",
          letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10,
        }}>
          #{rank}
        </div>
        <span onClick={() => onTicketClick && onTicketClick(ticket)}
          style={{
            fontFamily: MONO, fontSize: 16, fontWeight: 700,
            color: "#93C5FD", display: "block", marginBottom: 10,
            letterSpacing: "-0.01em", cursor: "pointer",
          }}>
          {ticket}
        </span>
        {summary && (
          <div style={{
            fontSize: 13, color: "rgba(255,255,255,0.72)", lineHeight: 1.5, marginBottom: 14,
            overflow: "hidden", display: "-webkit-box",
            WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}>
            {summary}
          </div>
        )}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <StatusPill label={priority} dark />
          <span style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.6)" }}>{age}</span>
        </div>
      </div>

      {/* CENTER — Analysis Zone */}
      <div style={{
        flex: "0 0 50%",
        padding: "22px 26px",
        background: G.blue50,
        borderRight: `1px solid ${G.blue100}`,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
          textTransform: "uppercase", color: G.gray600, marginBottom: 14,
        }}>
          Why Surfaced
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
          {reasons.length > 0 ? reasons.map((item, i) => (
            <span key={i} style={{
              padding: "5px 12px", borderRadius: 4,
              background: "#FFFFFF", color: G.blue700,
              fontSize: 13, fontWeight: 600,
              border: `1px solid ${G.brandBorder}`,
            }}>
              {item}
            </span>
          )) : (
            <span style={{ fontSize: 13, color: G.gray600, fontStyle: "italic" }}>
              Composite scoring
            </span>
          )}
        </div>
      </div>

      {/* RIGHT — Decision Area */}
      <div style={{
        flex: 1, minWidth: 240,
        padding: "22px 26px",
        background: "#FFFFFF",
        display: "flex", flexDirection: "column",
        alignItems: "flex-end", justifyContent: "space-between",
      }}>
        <div style={{ textAlign: "right" }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
            textTransform: "uppercase", color: G.gray600, marginBottom: 6,
          }}>
            Score
          </div>
          <div style={{
            fontSize: 46, fontWeight: 700, color: G.gray900,
            letterSpacing: "-0.04em", lineHeight: 1,
          }}>
            {score}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
          <div style={{ display: "flex", gap: 6 }}>
            <GhostBtn href={jiraUrl(ticket)}>Open Jira ↗</GhostBtn>
            <GhostBtn onClick={() => onTicketClick && onTicketClick(ticket)}>Open Lifecycle</GhostBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WorkRequiringAttention({
  criticalTickets = [],
  ageingCount = 0,
  ageingTickets = [],
  quickWinCount = 0,
  quickWinTickets = [],
  sortedPriorityTickets = [],
  prioritySort,
  onPrioritySortChange,
  onTicketClick,
}) {
  const panels = [
    {
      label: "Critical Issues",
      desc: "Open tickets whose age, priority, status and ownership add up to a risk score of 60 or higher.",
      count: criticalTickets.length, countUnit: "tickets require action", items: criticalTickets, accentColor: G.brand, type: "critical",
      expandHelp: "Sorted by risk score, highest first — start from the top of this list.",
      breakdown: {
        title: "How Risk Score is calculated",
        intro: "Each ticket starts at 0 and gains points from the factors below. Tickets scoring 60+ appear here.",
        factors: [
          { label: "Age", points: "+1 / 10 days (max +50)" },
          { label: "Priority", tiers: [
            { name: "Highest", value: "+25" },
            { name: "High", value: "+20" },
            { name: "Medium", value: "+10" },
            { name: "Low", value: "+5" },
          ] },
          { label: "Status", tiers: [
            { name: "On Hold", value: "+20" },
            { name: "In Progress", value: "+15" },
            { name: "To Do", value: "+10" },
            { name: "Testing", value: "+5" },
          ] },
          { label: "Issue type", tiers: [
            { name: "Bug", value: "+10" },
            { name: "Epic", value: "+8" },
            { name: "Story", value: "+6" },
            { name: "Task", value: "+4" },
            { name: "Sub-task", value: "+2" },
          ] },
          { label: "No assignee", points: "+10" },
        ],
        footnote: "Same factors shown per ticket under \"Why Flagged.\" Sorted highest score first, top 100 kept.",
      },
    },
    {
      label: "Ageing Work",
      desc: "Tickets open 30+ days, flagged once they pass their priority's SLA target.",
      count: ageingCount, countUnit: "tickets past threshold", items: ageingTickets, accentColor: G.brand, type: "ageing",
      expandHelp: "Sorted oldest first — the tickets furthest past their SLA target need review soonest.",
      breakdown: {
        title: "How SLA risk is calculated",
        intro: "Every ticket gets an SLA target based on its priority. Overdue days = ticket age − that target.",
        factors: [
          { label: "SLA target by priority", tiers: [
            { name: "Highest", value: "7d" },
            { name: "High", value: "30d" },
            { name: "Medium", value: "60d" },
            { name: "Low", value: "90d" },
            { name: "Lowest", value: "120d" },
          ] },
          { label: "Risk level by days overdue", tiers: [
            { name: "Over 180 days", value: "Critical" },
            { name: "Over 90 days", value: "High" },
            { name: "Over 30 days", value: "Warning" },
          ] },
        ],
        footnote: "Only tickets open 30+ days are included. Sorted by age, oldest first.",
      },
    },
    {
      label: "Quick Wins",
      desc: "Tickets already close to done (Testing, Review, UAT, In Progress) that can likely be closed with little extra work.",
      count: quickWinCount, countUnit: "opportunities available", items: quickWinTickets, accentColor: G.brand, type: "quickwins",
      expandHelp: "Sorted by win score, highest first — these are the fastest tickets to close.",
      breakdown: {
        title: "How Quick Win Score is calculated",
        intro: "Only tickets in Testing, Review, UAT or In Progress are considered. Points are capped at 100.",
        factors: [
          { label: "Stage", tiers: [
            { name: "Testing", value: "+40" },
            { name: "UAT", value: "+38" },
            { name: "Review", value: "+35" },
            { name: "In Progress", value: "+20" },
          ] },
          { label: "Age", tiers: [
            { name: "Under 30 days", value: "+25" },
            { name: "Under 60 days", value: "+18" },
            { name: "Under 90 days", value: "+10" },
          ] },
          { label: "Priority", tiers: [
            { name: "Highest", value: "+15" },
            { name: "High", value: "+12" },
            { name: "Medium", value: "+8" },
            { name: "Low", value: "+4" },
          ] },
          { label: "Has an assignee", points: "+10" },
          { label: "In Testing and under 30 days old", points: "+10 bonus" },
        ],
        footnote: "Same factors shown per ticket under \"Score Breakdown.\" Top 10 by score are shown here.",
      },
    },
  ];

  return (
    <>
      {/* Section 2: Action Center — sits one level below the Personal Developer
          Summary, on the light page background. */}
      <div style={{
        fontFamily: FONT_FAMILY,
        position: "relative",
        zIndex: 5,
        paddingBottom: 56,
      }}>
        <div data-tour="action-center" style={{
          maxWidth: OVERVIEW.contentMax,
          margin: "0 auto",
          padding: `0 ${OVERVIEW.gutter}px`,
          boxSizing: "border-box",
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 24,
        }}>
          {panels.map(panel => (
            <AttentionPanel key={panel.label} {...panel} onTicketClick={onTicketClick} />
          ))}
        </div>
      </div>

      {/* Section 3: Priority Workspace */}
      <OverviewSection background="page" divider>

        {/* Single enterprise card: header + all tickets */}
        <div data-tour="priority-workspace" style={{
          background: G.white,
          borderRadius: OVERVIEW.cardRadius,
          border: `1px solid ${OVERVIEW.cardBorder}`,
          boxShadow: OVERVIEW.cardShadow,
          overflow: "hidden",
        }}>

          {/* Card header */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "28px 32px",
            borderBottom: "1px solid #1A3557",
            background: "#071A2F",
          }}>
            <div>
              <Overline style={{ marginBottom: 8, color: "rgba(255,255,255,0.35)" }}>Priority Workspace</Overline>
              <SectionTitle light>
                {sortedPriorityTickets.length} tickets ranked by impact
              </SectionTitle>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", marginTop: 6 }}>
                Composite score · Identity · Analysis · Action
              </div>
              <Description light style={{ marginTop: 8, maxWidth: 480 }}>
                Every open ticket scored by age, priority, status, and ownership, ranked so the highest-impact work surfaces first.
              </Description>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10, flexShrink: 0 }}>
              <select value={prioritySort} onChange={e => onPrioritySortChange(e.target.value)}
                style={{ padding: "8px 14px", borderRadius: 4, border: "1px solid #1A3557", background: "#071A2F", fontSize: 13, color: "#FFFFFF", fontFamily: FONT_FAMILY, cursor: "pointer", outline: "none" }}>
                <option value="priority_score" style={{ background: "#071A2F", color: "#FFFFFF" }}>Highest Priority</option>
                <option value="ageing" style={{ background: "#071A2F", color: "#FFFFFF" }}>Oldest First</option>
                <option value="priority_weight" style={{ background: "#071A2F", color: "#FFFFFF" }}>Critical First</option>
                <option value="sla" style={{ background: "#071A2F", color: "#FFFFFF" }}>SLA First</option>
              </select>
              <ScoreBreakdown
                dark
                title="How Priority Score is calculated"
                intro="Each ticket starts at 0 and adds points from four factors. No cap — the 20 highest-scoring tickets are kept."
                factors={[
                  { label: "Age — over 365d / 180d / 90d / 30d", points: "+40 / +30 / +20 / +10" },
                  { label: "Priority — Highest / High / Medium / Low", points: "+25 / +20 / +10 / +5" },
                  { label: "Status — On Hold / To Do / In Progress / Testing", points: "+20 / +10 / +5 / +2" },
                  { label: "No assignee", points: "+15" },
                  { label: "Issue type — Bug / Epic / Story / Task", points: "+10 / +8 / +5 / +3" },
                ]}
                footnote={'Same factors shown per ticket under "Why Surfaced." Top 20 tickets by score, highest first.'}
              />
            </div>
          </div>

          {/* Ticket rows — show ~5, then scroll inside the card */}
          {sortedPriorityTickets.length === 0 ? (
            <div style={{ padding: 64, textAlign: "center", color: G.gray400, fontSize: 13, fontStyle: "italic" }}>
              No tickets to display
            </div>
          ) : (
            <div
              className="themed-scroll-light"
              style={{
                maxHeight: sortedPriorityTickets.length > 5 ? "min(78vh, 940px)" : "none",
                overflowY: sortedPriorityTickets.length > 5 ? "auto" : "visible",
              }}
            >
              {sortedPriorityTickets.map((ticket, index) => (
                <PriorityCard key={ticket.ticketNo}
                  rank={index + 1}
                  ticket={ticket.ticketNo}
                  priority={ticket.priority || "—"}
                  age={`${ticket.ageingDays || 0}d`}
                  score={String(ticket.priorityScore || 0)}
                  breakdown={ticket.breakdown || []}
                  summary={ticket.summary || ""}
                  isLast={index === sortedPriorityTickets.length - 1}
                  onTicketClick={onTicketClick}
                />
              ))}
            </div>
          )}
        </div>
      </OverviewSection>
    </>
  );
}
